import React, { useState } from "react";
import { Lightbulb, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { getPortfolioIdeas } from "@/lib/api";

const RISK_COLOR = { basso: "#00FF87", medio: "#F5A623", alto: "#FF4D4D" };

// Pannello "Idee di investimento" generate dall'AI (premium). Lazy: l'utente
// avvia la generazione (chiamata LLM ~ qualche secondo) e può rigenerare.
export const IdeasPanel = () => {
  const [ideas, setIdeas] = useState(null); // null = non caricato, [] = nessuna
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchIdeas = async (refresh = false) => {
    setLoading(true);
    try {
      const res = await getPortfolioIdeas(refresh);
      setIdeas(res.ideas || []);
      setMeta(res);
    } catch (e) {
      if (e?.response?.status === 402) toast.error("Funzione Pro: sblocca Liqo Pro");
      else toast.error("Impossibile generare gli spunti, riprova");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mt-4 rounded-[12px] border border-white/[0.06] bg-[#0D0D14] p-4" data-testid="invest-ideas-panel">
      <div className="flex items-center justify-between">
        <p className="caps-tag flex items-center gap-1.5 text-white/45">
          <Lightbulb size={12} className="text-[#00FF87]" />
          Idee di investimento · AI
        </p>
        {ideas !== null && (
          <button
            onClick={() => fetchIdeas(true)}
            disabled={loading}
            data-testid="invest-ideas-refresh"
            className="mono flex items-center gap-1 text-[10px] uppercase tracking-[0.12em] text-white/40 hover:text-white/70 disabled:opacity-50"
          >
            <RefreshCw size={11} className={loading ? "animate-spin" : ""} />
            Rigenera
          </button>
        )}
      </div>

      {ideas === null ? (
        <div className="mt-3">
          <p className="text-[12px] font-light leading-5 text-white/50">
            Spunti personalizzati generati dall'AI in base al tuo profilo di rischio, alla
            liquidità investibile e a ciò che già possiedi.
          </p>
          <button
            onClick={() => fetchIdeas(false)}
            disabled={loading}
            data-testid="invest-ideas-generate"
            className="mono mt-3 flex h-11 w-full items-center justify-center gap-2 rounded-[6px] border border-[#00FF87]/40 bg-[#00FF87]/[0.06] text-[11px] font-medium uppercase tracking-[0.16em] text-[#00FF87] hover:bg-[#00FF87]/[0.1] active:scale-[0.98] disabled:opacity-50"
          >
            {loading ? <RefreshCw size={13} className="animate-spin" /> : <Lightbulb size={13} />}
            {loading ? "Genero spunti…" : "Genera spunti AI"}
          </button>
        </div>
      ) : loading ? (
        <div className="mt-3 space-y-2" data-testid="invest-ideas-loading">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-20 w-full animate-pulse rounded-[8px] bg-white/[0.04]" />
          ))}
        </div>
      ) : ideas.length === 0 ? (
        <p className="mt-3 text-center text-[12px] font-light text-white/40">
          Nessuno spunto disponibile ora. Riprova tra poco.
        </p>
      ) : (
        <div className="mt-3 space-y-2">
          {ideas.map((idea, i) => {
            const rc = RISK_COLOR[idea.rischio] || "#F5A623";
            return (
              <div key={i} className="rounded-[8px] border border-white/[0.06] bg-[#050508] p-3" data-testid="invest-idea-card">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-[13px] font-light text-white/90">{idea.titolo}</p>
                    <p className="mono mt-0.5 text-[10px] uppercase tracking-[0.08em] text-white/35">
                      {idea.tipo}
                      {idea.ticker ? ` · ${idea.ticker}` : ""}
                    </p>
                  </div>
                  <div className="flex shrink-0 flex-col items-end gap-1">
                    {idea.allocazione_pct > 0 && (
                      <span className="mono text-[12px] tabular-nums text-[#00FF87]">{idea.allocazione_pct}%</span>
                    )}
                    <span
                      className="caps-tag rounded-[4px] px-1.5 py-0.5"
                      style={{ color: rc, border: `1px solid ${rc}55` }}
                    >
                      {idea.rischio}
                    </span>
                  </div>
                </div>
                <p className="mt-2 text-[12px] font-light leading-5 text-white/55">{idea.motivo}</p>
              </div>
            );
          })}
          {meta?.disclaimer && (
            <p className="mono mt-1 px-1 text-[10px] leading-4 tracking-[0.02em] text-white/25">{meta.disclaimer}</p>
          )}
        </div>
      )}
    </div>
  );
};
